/**
 * The grapple and the tow line.
 *
 * A crate is not an inventory icon that has been given a mesh. It is an object
 * in the water with a mass, and the only thing that ever moves it is a line
 * attached to the boat. Everything interesting about hauling comes out of that
 * one commitment: the load swings, it snags, it drags the trim down, it has to
 * be taken through the same tunnel you came in by, and it can be cut loose the
 * moment keeping it stops being worth it.
 *
 * The line is a **rope, not a spring.** It does nothing at all while there is
 * slack in it and applies a hard positional correction once it is taut. A
 * spring was the obvious first thing and it is wrong: any stiffness high enough
 * to stop a 1.5 t crate overtaking the sub also rings, and the load spends the
 * whole trip home oscillating on the end of a bungee.
 *
 * The correction is positional, mass-weighted, and runs **once per frame** —
 * deliberately not inside `Player.update`'s substep loop, where the step count
 * is `min(10, ceil(dist / 0.8525))` and therefore a function of how fast the
 * player happens to be going. Damping applied in there would be quietly
 * speed-dependent, which is the kind of bug that is felt long before it is
 * found.
 */

import { CARGO, GRAPPLE } from './config';
import { density, featuresFor } from './field';
import { toField } from './mesher';
import { clamp, hyp } from '../core/math';
import type { Pickup } from './entities';
import type { Player } from './player';

/** Scratch for lane-aware field lookups on the line and its load. */
const towField = { x: 0, y: 0 };

export interface TowLine {
  state: 'idle' | 'flying' | 'attached';
  /**
   * The lane the line is working in — the sub's, at the moment it was fired.
   *
   * The hook flies through water and stops on rock, and which rock that is
   * depends on the lane: on lane 2 the field is 1907.3 m along, so a hook
   * tested against lane 0 either sails through the wall in front of it or
   * stops dead in open water.
   */
  lane: number;
  /** The hook, while it is in flight. */
  hx: number; hy: number;
  hvx: number; hvy: number;
  /** Metres of line paid out, so the hook gives up at range. */
  travelled: number;
  load: Pickup | null;
  /** Current length of the line, in metres. */
  tether: number;
  /** How long GRAB has been held, to separate a tap from a winch. */
  held: number;
  /** 0..1 stretch toward parting. Drives the HUD strain bar and the snap. */
  strain: number;
  /** True on the frame the line parts, so the world can bang and shake. */
  snapped: boolean;
}

export interface TowHooks {
  onCatch: (p: Pickup) => void;
  /** The hook reached it and the gear could not lift it. */
  onTooHeavy: (p: Pickup, cap: number) => void;
  /** Line released deliberately. */
  onRelease: (p: Pickup | null) => void;
  /** Line parted under load, at the load's position. */
  onSnap: (x: number, y: number) => void;
}

export function newTowLine(): TowLine {
  return {
    state: 'idle',
    lane: 0,
    hx: 0, hy: 0, hvx: 0, hvy: 0,
    travelled: 0,
    load: null,
    tether: GRAPPLE.minRest,
    held: 0,
    strain: 0,
    snapped: false,
  };
}

const f = { x: 0, y: 0 };

/** Hand the load back to the world, moving at whatever the boat was doing. */
function release(tow: TowLine, p: Player) {
  const load = tow.load;
  if (load) {
    load.hold = 'free';
    // Inherit the boat's velocity rather than stopping dead — a crate let go
    // at speed should carry on, which is also how you throw one somewhere.
    load.vx = p.vx * 0.6;
    load.vy = p.vy * 0.6;
  }
  tow.state = 'idle';
  tow.load = null;
  tow.strain = 0;
  tow.held = 0;
}

/**
 * Fire, fly, catch, winch, cut. Runs after `Player.update` has moved the boat,
 * so the hook is tested against this frame's positions.
 */
export function driveTow(
  tow: TowLine,
  p: Player,
  pickups: readonly Pickup[],
  input: { grab: boolean; grabPressed: boolean },
  dt: number,
  hooks: TowHooks,
) {
  tow.snapped = false;

  // Dying drops whatever you were carrying, wherever you were carrying it.
  // Phase 4 turns that into a recoverable wreck; for now it simply falls.
  if (p.dead && tow.state !== 'idle') {
    const load = tow.load;
    release(tow, p);
    hooks.onRelease(load);
    return;
  }

  switch (tow.state) {
    case 'idle': {
      if (!input.grabPressed) break;
      // Off the belly port, not the nose, and biased downward: the hook is
      // lowered rather than fired. Aim still steers it, but half its initial
      // speed is straight down, so pointing roughly at something below and
      // paying out line is the natural way to use it.
      const a = p.aimAngle();
      p.fairlead(f);
      tow.hx = f.x;
      tow.hy = f.y;
      tow.hvx = Math.cos(a) * GRAPPLE.speed * 0.75 + p.vx;
      tow.hvy = Math.sin(a) * GRAPPLE.speed * 0.5 - GRAPPLE.speed * 0.5 + p.vy;
      tow.travelled = 0;
      // The line works in the lane it was fired from, and keeps it for the
      // whole flight — a hook that changed lanes mid-air would stop against
      // rock that was not there when it was aimed.
      tow.lane = Math.max(0, p.lane);
      tow.state = 'flying';
      break;
    }

    case 'flying': {
      // It is a weight on a cable: it falls as it goes, so the hook arcs down
      // to whatever is under the boat without having to be aimed at it.
      tow.hvy -= GRAPPLE.sink * dt;
      tow.hx += tow.hvx * dt;
      tow.hy += tow.hvy * dt;
      tow.travelled += hyp(tow.hvx, tow.hvy) * dt;

      // A second press recalls it; so does running out of line or hitting rock.
      // The hook does not dig into the seabed — a grapple that anchors to the
      // world is a completely different mechanic and not this one.
      const hf = toField(Math.max(0, tow.lane), tow.hx, tow.hy, towField);
      const feats = featuresFor(hf.x - 2, hf.x + 2);
      if (input.grabPressed || tow.travelled > GRAPPLE.range || density(hf.x, hf.y, feats) > 0) {
        tow.state = 'idle';
        hooks.onRelease(null);
        break;
      }

      for (const pk of pickups) {
        if (!pk.alive || pk.mass <= 0 || pk.hold !== 'free' || !pk.revealed) continue;
        // A line cannot reach through 431.7 m of rock, however close the two
        // look on screen.
        if (pk.lane !== tow.lane) continue;
        // Bigger loads are easier to hit, which is the right way round: the
        // thing you most want is also the thing you least want to fish for.
        const reach = GRAPPLE.catchRadius + pk.mass * 0.8;
        if (hyp(pk.x - tow.hx, pk.y - tow.hy) > reach) continue;

        // OVER THE RATING, SO THE HOOK DOES NOT CLOSE.
        //
        // Reported from here rather than silently skipped, and reported only
        // when the hook actually REACHED the thing — a refusal is information
        // the player has earned by lining the shot up, and it names the number
        // they need. Skipping quietly would read as the grapple being broken.
        if (pk.mass > p.stats.liftCap) {
          hooks.onTooHeavy(pk, p.stats.liftCap);
          break;
        }

        p.fairlead(f);
        tow.load = pk;
        pk.hold = 'towed';
        tow.state = 'attached';
        // The line is as long as the line that was paid out. Anything shorter
        // is already over-stretched the moment it is measured.
        tow.tether = clamp(hyp(pk.x - f.x, pk.y - f.y), GRAPPLE.minRest, GRAPPLE.range);
        // GRAB is almost certainly still down — it is what fired the hook. Seed
        // the timer past the tap threshold so that carried-over hold reads as a
        // winch and not as a deliberate cut; otherwise any press longer than
        // the hook's flight and shorter than 0.22 s attached and immediately
        // dropped the load, which is most normal presses.
        tow.held = GRAPPLE.tapTime;
        hooks.onCatch(pk);
        break;
      }
      break;
    }

    case 'attached': {
      const load = tow.load;
      if (!load || !load.alive) { release(tow, p); break; }

      // One button does both jobs, because every pad face button, trigger,
      // bumper and stick click is already spoken for. A tap cuts the line; a
      // hold winches it in. Holding is the safe default of the two — the
      // failure mode of a mistimed press is a shorter line, not a lost crate.
      if (input.grab) {
        tow.held += dt;
        if (tow.held >= GRAPPLE.tapTime) {
          // The boat's own winch, not a global one. A Dray's gantry takes line
          // in half again as fast as a survey boat's, which is most of what
          // makes hauling on it feel like the right tool.
          tow.tether = Math.max(GRAPPLE.minRest, tow.tether - p.chassis.winch * dt);
        }
      } else {
        if (tow.held > 0 && tow.held < GRAPPLE.tapTime) {
          release(tow, p);
          hooks.onRelease(load);
          break;
        }
        tow.held = 0;
      }
      break;
    }
  }
}

/**
 * One mass-weighted positional distance constraint, one iteration.
 *
 * No `dt`: a positional constraint is a statement about where things are, not
 * about how fast time is passing, and making it frame-rate dependent is how a
 * tow line ends up behaving differently at 30 and 120 fps.
 */
export function solveTow(tow: TowLine, p: Player, hooks: TowHooks) {
  const load = tow.load;
  if (tow.state !== 'attached' || !load) { tow.strain = 0; return; }
  // On the line, the load goes where the sub goes — including across a lane.
  // Dropped, it keeps whichever lane it was let go in and settles there.
  load.lane = Math.max(0, p.lane);

  p.fairlead(f);
  const dx = load.x - f.x;
  const dy = load.y - f.y;
  const len = hyp(dx, dy) || 1e-6;
  const excess = len - tow.tether;

  tow.strain = clamp(excess / GRAPPLE.snap, 0, 1);

  if (excess > GRAPPLE.snap) {
    tow.snapped = true;
    const lx = load.x, ly = load.y;
    release(tow, p);
    hooks.onSnap(lx, ly);
    return;
  }
  // Slack. A rope does nothing until it is taut, and this early return is the
  // whole reason a load swings around corners instead of ringing.
  if (excess <= 0) return;

  const nx = dx / len;
  const ny = dy / len;

  // The heavier body moves less — and "heavier" means THIS boat, not a global
  // constant. Weighting with `CARGO.dryMass` meant a 2.6 t Dray was yanked
  // around by a load exactly as hard as a 1.0 t survey boat, which quietly
  // removed the largest single reason to own one: at 4.6 t the correction
  // moves the boat 0.82 of the way with the global mass and 0.64 with the
  // Dray's own, and that difference is the whole feel of a working hull.
  const dry = p.chassis.dryMass;
  let wBoat = load.mass / (dry + load.mass);
  let wLoad = dry / (dry + load.mass);

  // A load fouled in rock takes none of the correction, so the sub takes all of
  // it and is simply stopped. Sharing it here would put the constraint in a
  // fight with the density push-out in `updatePickups`, and the crate would buzz
  // its way through the wall between them.
  const lf = toField(Math.max(0, load.lane), load.x, load.y, towField);
  const feats = featuresFor(lf.x - 3, lf.x + 3);
  if (density(lf.x, lf.y, feats) > -CARGO.crateRadius) {
    wBoat = 1;
    wLoad = 0;
  }

  p.x += nx * excess * wBoat;
  p.y += ny * excess * wBoat;
  load.x -= nx * excess * wLoad;
  load.y -= ny * excess * wLoad;

  // `Player.update` already integrated and resolved this frame, so this is the
  // one place that can put the hull back inside rock. Resolve again rather than
  // leaving it embedded until the next frame.
  if (wBoat > 0) p.resolveTerrain();

  // Kill only the component that is still pulling the two apart. Left in, the
  // pair separates again next frame and the line reads as elastic.
  const sep = (load.vx - p.vx) * nx + (load.vy - p.vy) * ny;
  if (sep > 0) {
    p.vx += nx * sep * wBoat;
    p.vy += ny * sep * wBoat;
    load.vx -= nx * sep * wLoad;
    load.vy -= ny * sep * wLoad;
  }
}
